/**
 * ThemeSelectorUI - 主题选择器组件
 * 提供主题切换功能的UI组件
 */

import { BootstrapUI, bootstrapUI, ElementOptions } from '../bootstrapui';
import { themeManager, ThemeConfig } from '../../styles/theme-config';

export interface ThemeSelectorOptions extends ElementOptions {
    /** 标签文本 */
    label?: string;
    /** 是否显示当前主题名称 */
    showCurrentTheme?: boolean;
    /** 主题选择器样式类 */
    selectorClass?: string;
    /** 主题预览样式类 */
    previewClass?: string;
}

export class ThemeSelectorUI {
    private ui: BootstrapUI;
    private container: HTMLElement;
    private selectElement: HTMLSelectElement;
    private options: ThemeSelectorOptions;
    private onChangeCallbacks: ((theme: ThemeConfig) => void)[] = [];

    constructor(options: ThemeSelectorOptions = {}) {
        this.ui = bootstrapUI;
        this.options = {
            label: '主题',
            showCurrentTheme: true,
            selectorClass: 'theme-selector',
            previewClass: 'theme-preview',
            ...options
        };

        this.container = this.createContainer();
        this.selectElement = this.createSelectElement();
        this.populateOptions();
        this.setupEventListeners();
    }

    private createContainer(): HTMLElement {
        const container = this.ui.createElement({
            className: 'form-item-container theme-selector-container'
        });

        if (this.options.label) {
            const label = this.ui.createLabel({
                text: this.options.label,
                for: 'theme-selector'
            });
            container.appendChild(label);
        }

        return container;
    }

    private createSelectElement(): HTMLSelectElement {
        const select = this.ui.createElement({
            tag: 'select',
            className: `form-select ${this.options.selectorClass || ''}`,
            attributes: {
                id: 'theme-selector'
            }
        }) as HTMLSelectElement;

        this.container.appendChild(select);
        return select;
    }

    private populateOptions(): void {
        try {
            const themes = themeManager.getAvailableThemes();
            const currentTheme = themeManager.getCurrentTheme();

            if (!themes || themes.length === 0) {
                console.warn('主题选择器：没有可用的主题');
                return;
            }

            if (!currentTheme) {
                console.warn('主题选择器：无法获取当前主题');
                return;
            }

            themes.forEach(theme => {
                const option = this.ui.createElement({
                    tag: 'option',
                    textContent: theme.name,
                    attributes: {
                        value: theme.name
                    }
                }) as HTMLOptionElement;

                this.selectElement.appendChild(option);
            });

            // 设置当前选中的主题
            this.selectElement.value = currentTheme.name;

            console.log('主题选择器选项已填充，主题数量:', themes.length);
        } catch (error) {
            console.error('主题选择器填充选项时出错:', error);
        }
    }

    private setupEventListeners(): void {
        this.selectElement.addEventListener('change', (event) => {
            const target = event.target as HTMLSelectElement;
            const themeName = target.value;
            
            console.log('主题选择器：用户选择了主题:', themeName);
            
            if (themeManager.setTheme(themeName)) {
                const theme = themeManager.getCurrentTheme();
                console.log('主题选择器：主题切换成功，当前主题:', theme.name);
                this.onChangeCallbacks.forEach(callback => callback(theme));
            } else {
                console.warn('主题选择器：主题切换失败:', themeName);
            }
        });

        // 监听主题变化事件
        window.addEventListener('themeChanged', (event: any) => {
            const theme = event.detail.theme;
            // 确保theme是对象，获取主题名称
            const newThemeName = (typeof theme === 'object' && theme !== null) ? theme.name : theme;
            
            console.log('主题选择器：收到主题变化事件:', { theme, newThemeName });
            
            if (newThemeName) {
                this.selectElement.value = newThemeName;
                console.log('主题选择器：已更新选中值:', this.selectElement.value);
            } else {
                console.warn('主题选择器：无效的主题名称:', newThemeName);
            }
        });
    }

    /**
     * 添加主题变化回调
     */
    onChange(callback: (theme: ThemeConfig) => void): void {
        this.onChangeCallbacks.push(callback);
    }

    /**
     * 获取当前选中的主题
     */
    getCurrentTheme(): ThemeConfig {
        return themeManager.getCurrentTheme();
    }

    /**
     * 设置主题
     */
    setTheme(themeName: string): boolean {
        if (themeManager.setTheme(themeName)) {
            this.selectElement.value = themeName;
            return true;
        }
        return false;
    }

    /**
     * 设置组件激活状态（启用/禁用整个组件）
     */
    setActive(status: boolean): void {
        if (!status) {
            // 禁用整个组件
            this.container.classList.add('forbidden-event-dom');
            
            // 禁用下拉框
            this.selectElement.disabled = true;
        } else {
            // 启用整个组件
            this.container.classList.remove('forbidden-event-dom');
            
            // 启用下拉框
            this.selectElement.disabled = false;
        }
    }

    /**
     * 获取容器元素
     */
    getContainer(): HTMLElement {
        return this.container;
    }

    /**
     * 销毁组件
     */
    destroy(): void {
        this.onChangeCallbacks = [];
        if (this.container.parentNode) {
            this.container.parentNode.removeChild(this.container);
        }
    }
}
